import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../store/useStore';
import { supabase } from '../../config/supabase';
import { logger } from '../../utils/logger';
import toast from 'react-hot-toast';

export const useSignIn = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { setUser } = useStore();

  const signIn = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Validate input
      if (!email.trim() || !password.trim()) {
        throw new Error('Please enter both email and password');
      }

      // Sign in with Supabase
      const { data: { user }, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (signInError) {
        logger.error('Sign in error:', signInError);
        throw new Error('Invalid email or password');
      }

      if (!user) {
        throw new Error('No user data returned');
      }

      // Get user profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) {
        logger.error('Profile fetch error:', profileError);
        throw new Error('Failed to load user profile');
      }

      if (!profile) {
        throw new Error('Profile not found');
      }

      // Set user in store
      const userData = {
        id: profile.id,
        email: profile.email,
        name: profile.name,
        role: profile.role,
        department: profile.department,
        avatar: profile.avatar || `https://api.dicebear.com/7.x/avatars/svg?seed=${profile.email}`,
        phone: profile.phone
      };

      setUser(userData);
      
      // Show success message
      toast.success(`Welcome back, ${profile.name}!`);
      
      // Redirect based on role
      navigate('/', { replace: true });

      return userData;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to sign in';
      logger.error('Sign in error:', error);
      setError(message);
      toast.error(message);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { 
    signIn, 
    isLoading, 
    error 
  };
};